import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Wand2, Loader2, Lock, Rocket, FolderKanban, Swords, Mic, Award, Sparkles, PenLine } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const AIChallengeBuilderSection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done
  const [challenge, setChallenge] = useState(null);
  const [dayTitles, setDayTitles] = useState([]);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      toast.error("Tell the AI what you want to master first.");
      return;
    }
    if (!user) {
      sessionStorage.setItem("pending_challenge_topic", topic.trim());
      toast.message("Create a free account — we'll generate your challenge the moment you're in.");
      navigate("/register");
      return;
    }
    setState("loading");
    try {
      const { data } = await api.post("/custom-challenges", { topic: topic.trim() });
      setChallenge(data.challenge);
      setDayTitles(data.day_titles || []);
      setState("done");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't generate your challenge — please try again.");
      setState("idle");
    }
  };

  return (
    <section id="ai-challenge-builder" className="max-w-5xl mx-auto px-6 py-24" data-testid="ai-challenge-builder-section">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 flex items-center justify-center gap-2">
          <Wand2 className="w-3.5 h-3.5" /> AI Challenge Builder
        </span>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3">Build Your Own Challenge</h2>
        <p className="text-slate-400 mt-3">Tell the AI what you want to master — it drafts a full 30-day roadmap with lessons, quizzes, projects, and a Boss Battle, ready to publish.</p>
      </div>

      <div className="rounded-3xl border border-white/10 bg-[#0A101D]/70 backdrop-blur-xl p-6 sm:p-8">
        <label className="text-xs font-semibold text-slate-400 mb-2 block">What do you want to master?</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            value={topic} onChange={(e) => setTopic(e.target.value)} data-testid="ai-challenge-builder-input"
            placeholder="Become an AI Engineer in 30 Days"
            onKeyDown={(e) => e.key === "Enter" && handleGenerate()}
            className="flex-1 bg-white/[0.04] border border-white/10 focus:border-[#5C9DFF]/60 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors"
          />
          <button
            onClick={handleGenerate} data-testid="ai-challenge-builder-generate-btn" disabled={state === "loading"}
            className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5 shrink-0"
          >
            {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Generate with AI
          </button>
        </div>

        <AnimatePresence mode="wait">
          {state === "loading" && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-8 text-center py-10" data-testid="ai-challenge-builder-loading">
              <Loader2 className="w-6 h-6 text-[#5C9DFF] animate-spin mx-auto mb-3" />
              <p className="text-sm text-slate-400">Real AI is designing your 30-day roadmap — this takes ~15-20 seconds...</p>
            </motion.div>
          )}

          {state === "done" && challenge && (
            <motion.div key="done" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="ai-challenge-builder-result">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C9DFF]">{challenge.category}</span>
              <h3 className="font-heading text-xl font-semibold text-white mt-1" data-testid="ai-challenge-builder-title">{challenge.title}</h3>
              <p className="text-sm text-slate-400 mt-1.5 mb-6">{challenge.description}</p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-6">
                {dayTitles.slice(0, 8).map((title, i) => (
                  <div key={i} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`ai-roadmap-day-${i + 1}`}>
                    <span className="text-[10px] font-bold text-[#5C9DFF]">DAY {i + 1}</span>
                    <p className="text-[11px] text-white leading-snug mt-1">{title}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-6">
                <Lock className="w-3 h-3" /> +{Math.max(dayTitles.length - 8, 0)} more days generated as you reach them — Day 9 through Day 30
              </div>

              <div className="flex flex-wrap gap-3 mb-8">
                {[["Projects", FolderKanban], ["Boss Battles", Swords], ["Mock Interviews", Mic], ["Certificate", Award]].map(([label, Icon]) => (
                  <span key={label} className="flex items-center gap-1.5 text-xs font-semibold text-slate-300 bg-white/[0.03] border border-white/10 rounded-full px-3 py-1.5">
                    <Icon className="w-3.5 h-3.5 text-emerald-400" /> {label}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to={`/creator/challenges/${challenge.id}/builder`} data-testid="ai-challenge-builder-customize-btn"
                  className="flex-1 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5"
                >
                  <PenLine className="w-4 h-4" /> Customize This Challenge
                </Link>
                <Link
                  to={`/custom-challenges/${challenge.id}`} data-testid="ai-challenge-builder-start-learning-btn"
                  className="flex-1 flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-6 py-3.5 rounded-xl transition-all hover:-translate-y-0.5"
                >
                  Start Learning — Day 1 <Rocket className="w-4 h-4" />
                </Link>
              </div>
              <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5"><Lock className="w-3 h-3" /> This is a draft — review and edit any day in the Builder before publishing to the Marketplace.</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
